import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useAdminLoad from "@/hooks/useAdminLoad";
import { useAdmin } from "@/admin/AuthContext";
import { LoadState, Panel, Pill } from "@/components/admin/AdminUI";
import UserStatus from "@/components/admin/UserStatus";
import UserActions from "@/components/admin/UserActions";
import UserFacts from "@/components/admin/UserFacts";
import ProfilePanel from "@/components/admin/ProfilePanel";
import TierPanel from "@/components/admin/TierPanel";
import ActiveBans from "@/components/admin/ActiveBans";
import UserTools from "@/components/admin/user/UserTools";
import NotesPanel from "@/components/admin/user/NotesPanel";
import LaunchFacts from "@/components/admin/user/LaunchFacts";
import PurchasesTab from "@/components/admin/user/PurchasesTab";
import WarnDialog from "@/components/admin/user/WarnDialog";
import WarningsFact from "@/components/admin/user/WarningsFact";
import { DevicesTab, ReportsTab, VerificationTab } from "@/components/admin/user/UserTabs";

function Bans({ items }) {
  if (!items?.length) return <p className="cw-muted text-sm">No ban records created on this account.</p>;
  return (
    <ul className="divide-y divide-border" data-testid="user-bans">
      {items.map((b) => (
        <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
          <span><span className="capitalize">{b.type}</span> · <span className="cw-soft break-all">{b.value || b.value_masked}</span></span>
          <span className="flex items-center gap-2"><Pill tone={b.active ? "error" : "neutral"}>{b.active ? "Active" : "Lifted"}</Pill><span className="cw-muted">{b.reason}</span></span>
        </li>
      ))}
    </ul>
  );
}

const isActive = (g) => !g.revoked_at && (!g.expires_at || new Date(g.expires_at) > new Date());
const TABS = [["profile", "Profile"], ["tier", "Tier and grants"], ["bans", "Active bans"], ["reports", "Reports"], ["verification", "Verification"], ["purchases", "Purchases"], ["devices", "Devices"], ["notes", "Notes"]];

function Header({ data, role, canModerate, onUser, reload, onWarned }) {
  const [warning, setWarning] = useState(false);
  const u = data.user;
  const name = data.profile?.first_name;
  const launchGrant = (data.grants || []).some((g) => g.source === "launch_grant" && isActive(g));
  return (
    <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl" data-testid="user-detail-name">{name || "No name yet"}<span className="cw-muted ml-3 text-lg font-normal tabular-nums" data-testid="user-detail-phone">{u.phone}</span></h1>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <UserStatus user={u} />
          {data.test_account && <Pill data-testid="user-test-badge">Test account</Pill>}
          {data.founding?.founding_crush && <Pill tone="brand" data-testid="user-founding-badge">Founding Crush{data.founding.number ? ` #${data.founding.number}` : ""}</Pill>}
          {launchGrant && <Pill tone="success" data-testid="user-launch-grant-badge">Launch grant</Pill>}
          <span className="cw-muted text-xs" data-testid="user-detail-id">{u.id}</span>
          {role === "owner" && <Link to={`/admin/notifications?user=${u.id}`} className="cw-brand-deep text-xs font-medium underline-offset-2 hover:underline" data-testid="user-detail-push-log-link">Push log</Link>}
        </div>
      </div>
      <div className="flex flex-col items-end gap-3">
        {canModerate ? (
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => setWarning(true)} data-testid="user-warn-button">Warn</Button>
            <UserActions user={u} onUser={onUser} onBanned={reload} />
          </div>
        ) : <Pill tone="neutral" data-testid="user-readonly-note">No moderation actions for your role</Pill>}
        {canModerate && <WarnDialog open={warning} onOpenChange={setWarning} userId={u.id} onWarned={onWarned} />}
        <UserTools user={u} role={role} firstName={name} onChanged={reload} />
      </div>
    </header>
  );
}

export default function UserDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const { admin, permissions } = useAdmin();
  const role = admin?.role;
  const { data, error, loading, reload, setData } = useAdminLoad(`/admin/users/${id}`);
  const onUser = (user) => setData((d) => ({ ...d, user }));
  const onWarned = () => reload();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "profile";

  return (
    <div data-testid="admin-user-detail-page">
      <Link to="/admin/users" className="cw-soft mb-6 inline-flex items-center gap-2 rounded text-sm hover:text-foreground" data-testid="user-detail-back-link"><ArrowLeft size={16} />All users</Link>
      <LoadState loading={loading && !data} error={error} onRetry={reload}>
        {data && (
          <div className="space-y-6">
            <Header data={data} role={role} canModerate={!!permissions?.users_moderate} onUser={onUser} reload={reload} onWarned={onWarned} />
            <Tabs value={tab} onValueChange={(v) => setParams({ tab: v }, { replace: true })}>
              <TabsList className="h-auto flex-wrap" data-testid="user-detail-tabs">{TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`user-tab-${k}`}>{l}{k === "bans" && data.active_bans?.length ? ` (${data.active_bans.length})` : ""}</TabsTrigger>)}</TabsList>
              <TabsContent value="profile" className="mt-6 space-y-6"><ProfilePanel data={data} /><UserFacts user={data.user} /><WarningsFact user={data.user} loading={loading} /><LaunchFacts data={data} isOwner={role === "owner"} onChanged={reload} /></TabsContent>
              <TabsContent value="tier" className="mt-6"><TierPanel userId={id} role={role} /></TabsContent>
              <TabsContent value="bans" className="mt-6 space-y-6">
                <Panel className="p-6" data-testid="user-active-bans-panel"><h2 className="font-semibold">Active bans</h2>
                  <p className="cw-soft mb-3 mt-1 text-sm">Every active phone or device ban that matches this person, including bans made on other accounts.</p><ActiveBans items={data.active_bans} /></Panel>
                <Panel className="p-6"><h2 className="mb-3 font-semibold">Ban records on this account</h2><Bans items={data.bans} /></Panel>
              </TabsContent>
              <TabsContent value="reports" className="mt-6"><ReportsTab data={data} /></TabsContent>
              <TabsContent value="verification" className="mt-6"><VerificationTab data={data} role={role} /></TabsContent>
              <TabsContent value="purchases" className="mt-6"><PurchasesTab data={data} /></TabsContent>
              <TabsContent value="devices" className="mt-6"><DevicesTab data={data} /></TabsContent>
              <TabsContent value="notes" className="mt-6"><NotesPanel key={id} userId={id} initial={data.notes} /></TabsContent>
            </Tabs>
          </div>
        )}
      </LoadState>
    </div>
  );
}
